import * as React from 'react';
import BPromise from 'bluebird';
import { Loader } from 'Src/components/Loader/Loader';
import { getAllSuperAdminSchoolSports, SuperAdminSport } from 'Src/helpers/service/superadmin/sports';
import { superAdminGet, superAdminPut } from 'Src/helpers/service/superadmin/api';

function getSchoolAllowedSportIds(schoolId: string): Promise<string[]> {
  return superAdminGet<string[]>(`/schools/${schoolId}/allowedSports`);
}

function setSchoolAllowedSports(schoolId: string, sportIds: string[]): Promise<string[]> {
  return superAdminPut<string[]>(`/schools/${schoolId}/allowedSports`, { sportIds });
}

interface Props {
  schoolId: string;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  allowedSports: SuperAdminSport[];
  otherSports: SuperAdminSport[];
  errorMessage: string;
  successMessage: string;
}

export class SuperAdminSchoolAllowedSports extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: false,
      allowedSports: [],
      otherSports: [],
      errorMessage: '',
      successMessage: ''
    };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    BPromise.all([getSchoolAllowedSportIds(this.props.schoolId), getAllSuperAdminSchoolSports(this.props.schoolId)])
      .then(([allowedIds, allSports]) => {
        const allowedSports = allSports.filter(s => allowedIds.includes(s.id));
        const otherSports = allSports.filter(s => !allowedIds.includes(s.id));
        this.setState({ isLoading: false, allowedSports, otherSports });
      })
      .catch(err => {
        console.error('Cannot load allowed sports', err);
        this.setState({ isLoading: false, errorMessage: 'Could not load sports.' });
      });
  };

  onMoveToAllowed = (index: number) => {
    this.setState(prev => {
      const sport = prev.otherSports[index];
      return {
        allowedSports: [...prev.allowedSports, sport],
        otherSports: prev.otherSports.filter((_, i) => i !== index)
      };
    });
  };

  onMoveToOther = (index: number) => {
    this.setState(prev => {
      const sport = prev.allowedSports[index];
      return {
        otherSports: [sport, ...prev.otherSports],
        allowedSports: prev.allowedSports.filter((_, i) => i !== index)
      };
    });
  };

  onSave = () => {
    const sportIds = this.state.allowedSports.map(s => s.id);
    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    setSchoolAllowedSports(this.props.schoolId, sportIds)
      .then(() => {
        this.setState({ isSaving: false, successMessage: 'Allowed sports saved successfully.' });
      })
      .catch(err => {
        console.error('Cannot save allowed sports', err);
        this.setState({ isSaving: false, errorMessage: 'Could not save. Please try again.' });
      });
  };

  renderList(
    title: string,
    sports: SuperAdminSport[],
    onRowClick: (index: number) => void,
    actionLabel: string,
    badgeClass: string
  ) {
    return (
      <div className="flex-fill" style={{ minWidth: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{title}</strong>
          <span className={`badge ${badgeClass}`}>{sports.length}</span>
        </div>
        <div
          style={{
            border: '1px solid #dee2e6',
            borderRadius: 4,
            minHeight: 200,
            maxHeight: 480,
            overflowY: 'auto'
          }}
        >
          {sports.length === 0 ? (
            <div className="text-muted small p-3 text-center">No sports</div>
          ) : (
            <table className="table table-sm table-hover mb-0">
              <tbody>
                {sports.map((sport, index) => (
                  <tr
                    key={sport.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onRowClick(index)}
                    title={actionLabel}
                  >
                    <td>{sport.name || '—'}</td>
                    <td style={{ width: 60, textAlign: 'right' }}>
                      <span className="text-muted small">{actionLabel}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, isSaving, allowedSports, otherSports, errorMessage, successMessage } = this.state;

    if (isLoading) return <Loader />;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Allowed sports</h4>
            <div className="text-muted small">Click a sport to move it between lists. Save when done.</div>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={this.load} disabled={isSaving}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <div className="d-flex" style={{ gap: 16 }}>
          {this.renderList('Allowed sports', allowedSports, this.onMoveToOther, '✕ Remove', 'badge-success')}

          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ flexShrink: 0, color: '#aaa', fontSize: 20 }}
          >
            ⇄
          </div>

          {this.renderList('All other sports', otherSports, this.onMoveToAllowed, '+ Allow', 'badge-secondary')}
        </div>

        <div className="mt-3">
          <button className="btn btn-primary btn-sm" onClick={this.onSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  }
}

